import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { gql } from "../../__generated__";
import { AiAnimatedElementsGeneration } from "../../__generated__/graphql";

const GET_CATEGORIES = gql(`
  query GetRevealsCategoriesWithPresets($filters: GetRevealsCategoriesWithPresetsFilters) {
    getRevealsCategoriesWithPresets(filters: $filters) {
      categories {
        categoryId
        description
        isHighlighted
        label
        presets {
          categoryIds
          presetId
          presetFlow
          isPresetNew
          modelData {
            jsonPromptObject
          }
          thumbnailData {
            thumbnailUrl
          }
          previewVideoForCategory {
            url
          }
        }
      }
    }
  }
`);

export function useCategories() {
  return useQuery(GET_CATEGORIES, {
    variables: {
      filters: {
        showHidden: import.meta.env.VITE_SENTRY_ENVIRONMENT === "staging",
        showHiddenPresets:
          import.meta.env.VITE_SENTRY_ENVIRONMENT === "staging",
      },
    },
  });
}

const CREATE_GENERATION_PREVIEW = gql(`
  mutation CreateAiAnimatedElementsWithPresetsPreview(
    $inputFileUrl: String!
    $presets: [PresetConfig!]
    $inputBucketInfo: BucketInfoInput
    $needsIntermediateFramesValidation: IntermediateFramesValidationInput
  ) {
    createAiAnimatedElementsWithPresetsAnonymous(
      inputFileUrl: $inputFileUrl
      presets: $presets
      inputBucketInfo: $inputBucketInfo,
      needsIntermediateFramesValidation: $needsIntermediateFramesValidation
    ) {
      _id
      progress
      generationId
      inputFileUrl
      inputBucketInfo {
        bucketName
        filePath
      }
      status
      feedback {
        rating
        message
      }
      presets {
        aspectRatio
        imagePosition
        margin
        customPrompt
        coordinates {
          x
          y
        }
        backgroundColor
        presetId
        modelName
        audioTask {
          status
          jsonPayload
        }
        videoDuration
        task {
          status
          parsedResult
          jsonPayload
          intermediateResults {
            url
          }
          thumbnailData {
            thumbHash
            thumbnailUrl
          }
          metadata
        }
      }
    }
  }
`);

export function useCreateGenerationPreview() {
  return useMutation(CREATE_GENERATION_PREVIEW);
}

const CREATE_PREVIEW_FROM_TEXT = gql(`
  mutation CreateAnimatedVideoFromText($textInput: String!, $preset: PresetConfig!) {
    createAnimatedVideoFromTextAnonymous(textInput: $textInput, preset: $preset) {
      _id
      progress
      generationId
      inputFileUrl
      inputBucketInfo {
        bucketName
        filePath
      }
      status
      feedback {
        rating
        message
      }
      presets {
        aspectRatio
        imagePosition
        margin
        customPrompt
        coordinates {
          x
          y
        }
        backgroundColor
        presetId
        modelName
        audioTask {
          status
          jsonPayload
        }
        videoDuration
        task {
          status
          parsedResult
          jsonPayload
          intermediateResults {
            url
          }
          thumbnailData {
            thumbHash
            thumbnailUrl
          }
          metadata
        }
      }
    }
  }
`);

export function useCreatePreviewFromText() {
  return useMutation(CREATE_PREVIEW_FROM_TEXT);
}

const CREATE_PREVIEW_FROM_PROMPT = gql(`
  mutation CreateAnimatedVideoFromPrompt($preset: PresetConfig!) {
  createAnimatedVideoFromPromptAnonymous(preset: $preset) {
    _id
      progress
      generationId
      inputFileUrl
      inputBucketInfo {
        bucketName
        filePath
      }
      status
      feedback {
        rating
        message
      }
      presets {
        aspectRatio
        imagePosition
        margin
        customPrompt
        coordinates {
          x
          y
        }
        backgroundColor
        presetId
        modelName
        audioTask {
          status
          jsonPayload
        }
        videoDuration
        task {
          status
          parsedResult
          jsonPayload
          intermediateResults {
            url
          }
          thumbnailData {
            thumbHash
            thumbnailUrl
          }
          metadata
        }
      }
  }
}`);

export function useCreatePreviewFromPrompt() {
  return useMutation(CREATE_PREVIEW_FROM_PROMPT);
}

export function getGenerationPreview(generation: AiAnimatedElementsGeneration) {
  const payload = JSON.parse(generation.presets[0].task?.jsonPayload ?? "") as {
    input: { promptImageAlternatives?: { uri?: string }[] };
  };
  const alternatives = payload.input.promptImageAlternatives;

  return {
    generationId: generation.generationId,
    url: alternatives?.[alternatives?.length - 1]?.uri,
  };
}

const START_PREVIEW_GENERATION = gql(`
  mutation StartPendingGeneration($generationId: ID!, $previewUrl: String!, $withAudio: Boolean, $videoDuration: Int) {
    startPendingGenerationAnonymous(generationId: $generationId, previewUrl: $previewUrl, withAudio: $withAudio, videoDuration: $videoDuration) {
      _id
      progress
      generationId
      inputFileUrl
      inputBucketInfo {
        bucketName
        filePath
      }
      status
      feedback {
        rating
        message
      }
      presets {
        aspectRatio
        imagePosition
        margin
        customPrompt
        coordinates {
          x
          y
        }
        backgroundColor
        presetId
        modelName
        audioTask {
          status
          jsonPayload
        }
        videoDuration
        task {
          status
          parsedResult
          jsonPayload
          intermediateResults {
            url
          }
          thumbnailData {
            thumbHash
            thumbnailUrl
          }
          metadata
        }
      }
    }
  }
`);

export function useStartGeneration() {
  return useMutation(START_PREVIEW_GENERATION);
}

export enum GenerationStatus {
  Preparing = "preparing",
  Queued = "queued",
  Complete = "complete",
  Generating = "processing",
  AudioQueued = "audioQueued",
  AudioProcessing = "audioProcessing",
  Canceled = "canceled",
  Error = "error",
}

const UPLOAD_IMAGE_BASE64 = gql(`
  query GenerateAiAnimatedPictureInputFromBase64($base64Data: String!, $fileName: String!) {
  generateAiAnimatedPictureInputFromBase64(base64Data: $base64Data, fileName: $fileName) {
    removedBackgroundPictureUrl
    bucketName
    filePath
    backgroundColorToUse
  }
}
`);

export function useUploadBase64Encoded() {
  return useLazyQuery(UPLOAD_IMAGE_BASE64);
}

export function base64ToUrl(
  base64: string | undefined,
  contentType = "",
  sliceSize = 512,
) {
  if (!base64) return null;
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return URL.createObjectURL(blob);
}

const GET_PRESET_SUGGESTION_FROM_IMAGE = gql(`
  query GetPresetSuggestionsFromImage($imageUrl: String!) {
  getPresetSuggestionsFromImage(imageUrl: $imageUrl) {
    long
    object
    short
    suggestions {
      categoryIds
      presetId
      presetFlow
      isPresetNew
      modelData {
        jsonPromptObject
      }
      previewVideoUrl
      previewVideoForCategory {
        url
      }
    }
  }
}
`);

export function useGetPromptPresetsSuggestions(base64: string) {
  return useQuery(GET_PRESET_SUGGESTION_FROM_IMAGE, {
    variables: { imageUrl: base64 },
  });
}

const GET_PRESET_SUGGESTION_FROM_TEXT = gql(`
  query GetPresetSuggestionsFromText($text: String!) {
  getPresetSuggestionsFromText(text: $text) {
    suggestions {
      categoryIds
      presetId
      presetFlow
      isPresetNew
      modelData {
        jsonPromptObject
      }
      previewVideoUrl
      previewVideoForCategory {
        url
      }
    }
  }
}`);

export function useGetPromptPresetsSuggestionsFromText(text: string) {
  return useQuery(GET_PRESET_SUGGESTION_FROM_TEXT, { variables: { text } });
}

const CREATE_IMAGE_TO_VIDEO_IMMEDIATE = gql(`
  mutation CreateAnimatedVideoFromImageImmediate($imageUrl: String!, $inputBucketInfo: BucketInfoInput!, $preset: PresetConfig!) {
    createAnimatedVideoFromImageImmediate(imageUrl: $imageUrl, inputBucketInfo: $inputBucketInfo, preset: $preset) {
       _id
      progress
      generationId
      inputFileUrl
      inputBucketInfo {
        bucketName
        filePath
      }
      status
      feedback {
        rating
        message
      }
      presets {
        aspectRatio
        imagePosition
        margin
        customPrompt
        coordinates {
          x
          y
        }
        backgroundColor
        presetId
        modelName
        audioTask {
          status
          jsonPayload
        }
        videoDuration
        task {
          status
          parsedResult
          jsonPayload
          intermediateResults {
            url
          }
          thumbnailData {
            thumbHash
            thumbnailUrl
          }
          metadata
        }
      }
    }
  }  
`);

export function useCreateImageToVideoImmediate() {
  return useMutation(CREATE_IMAGE_TO_VIDEO_IMMEDIATE);
}
